<template>
  <div>
    <h2 class="page-title">
      <el-icon><DataAnalysis /></el-icon>
      检测结果
    </h2>

    <div class="page-card">
      <div class="table-operations">
        <div class="query-controls">
          <el-input
            v-model="searchForm.deviceId"
            placeholder="输入设备ID搜索"
            style="width: 250px"
            clearable
            @clear="handleSearch"
            @change="handleSearch"
            @keyup.enter="handleSearch"
            aria-label="设备ID搜索"
          >
            <template #append>
              <el-button :icon="Search" @click="handleSearch" />
            </template>
          </el-input>
          <el-config-provider :locale="zhCn">
            <div class="date-controls">
              <el-select
                v-model="dateMode"
                class="date-mode"
                aria-label="时间查询方式"
                @change="handleDateModeChange"
              >
                <el-option label="按日" value="day" />
                <el-option label="按月" value="month" />
                <el-option label="按年" value="year" />
                <el-option label="日期范围" value="range" />
              </el-select>
              <el-date-picker
                :key="dateMode"
                v-model="selectedDate"
                :type="datePickerType"
                :format="dateDisplayFormat"
                value-format="YYYY-MM-DD"
                :placeholder="datePlaceholder"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                range-separator="至"
                :disabled-date="isDateDisabled"
                :default-value="calendarDefaultDate"
                :disabled="calendarLoading"
                :editable="false"
                :class="[
                  'result-date-picker',
                  { 'is-range': dateMode === 'range' },
                ]"
                aria-label="检测时间查询"
                clearable
                @change="handleSearch"
              />
            </div>
          </el-config-provider>
          <span class="calendar-hint">{{
            calendarLoading ? "正在读取记录日期…" : "灰色日期暂无记录"
          }}</span>
        </div>
        <el-button :icon="Refresh" @click="handleRefresh">重置</el-button>
      </div>

      <p class="batch-list-hint">
        同批次图片合并为一条；列表显示首张图片的检测结果，详情可逐张切换查看。
      </p>

      <el-table :data="resultList" stripe v-loading="loading">
        <template #empty>
          <el-empty description="暂无符合条件的检测记录" :image-size="80" />
        </template>
        <el-table-column label="机器名" width="140" align="center">
          <template #default="{ row }">{{ recordDeviceName(row) }}</template>
        </el-table-column>
        <el-table-column label="图片批次" width="150" align="center">
          <template #default="{ row }">
            <div class="batch-status">
              <el-tag
                v-if="row.grouped"
                :type="row.complete ? 'success' : 'warning'"
                size="small"
              >
                已上传 {{ row.uploadedCount }}/{{ row.expectedCount }}
              </el-tag>
              <el-tag v-else type="info" size="small">单张记录</el-tag>
              <small v-if="row.duplicateSequences?.length"
                >含重复序号，共{{ row.imageCount }}张</small
              >
            </div>
          </template>
        </el-table-column>
        <el-table-column label="虾体数量" width="110" align="center">
          <template #default="scope">
            <span class="count-value">{{
              getShrimpCount(scope.row) ?? "-"
            }}</span>
          </template>
        </el-table-column>
        <el-table-column label="判断概览" min-width="260">
          <template #default="scope">
            <el-tag
              v-if="getShrimpCount(scope.row) === 0"
              type="info"
              size="small"
            >
              未检测到虾
            </el-tag>
            <div v-else-if="getShrimps(scope.row).length" class="summary-tags">
              <el-tag
                v-for="item in getAssessmentSummary(scope.row)"
                :key="item.code"
                :type="getOverallTagType(item.code)"
                size="small"
                effect="light"
              >
                {{ item.label }} {{ item.count }}
              </el-tag>
            </div>
            <span v-else class="legacy-label">尚无单虾评分</span>
          </template>
        </el-table-column>
        <el-table-column label="原图" width="90" align="center">
          <template #default="scope">
            <DataImage
              v-if="scope.row.originalImage"
              class="table-image"
              :image-id="scope.row.id"
              :version="scope.row.originalImage"
              thumbnail
              fit="cover"
            />
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="识别图" width="90" align="center">
          <template #default="scope">
            <DataImage
              v-if="scope.row.annotatedImage"
              class="table-image"
              :image-id="scope.row.id"
              :version="scope.row.annotatedImage"
              variant="annotated"
              thumbnail
              fit="cover"
            />
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="批次 / 检测时间" width="180">
          <template #default="scope">{{
            formatDate(scope.row.batchTime || scope.row.createdTime)
          }}</template>
        </el-table-column>
        <el-table-column label="操作" width="180" fixed="right" align="center">
          <template #default="scope">
            <div class="row-actions">
              <el-button
                size="small"
                :icon="View"
                @click="handleViewDetail(scope.row)"
              >
                详情
              </el-button>
              <el-button
                v-if="!scope.row.grouped"
                size="small"
                type="danger"
                :icon="Delete"
                @click="handleDelete(scope.row)"
              >
                删除
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination">
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          :layout="
            counting
              ? 'sizes, prev, pager, next, jumper'
              : 'total, sizes, prev, pager, next, jumper'
          "
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>

    <el-dialog
      v-model="detailDialogVisible"
      title="检测结果详情"
      width="900px"
      top="5vh"
      destroy-on-close
      @close="cancelDetailLoad"
    >
      <el-skeleton v-if="detailLoading && !currentBatch" :rows="7" animated />
      <div v-if="currentBatch" class="batch-navigation">
        <div class="batch-navigation-heading">
          <div>
            <strong>{{
              currentBatch.grouped
                ? "批次 " + currentBatch.batchName
                : "单张记录"
            }}</strong
            ><span
              >设备 {{ recordDeviceName(currentBatch) }} ·
              {{
                formatDate(currentBatch.batchTime || currentBatch.createdTime)
              }}</span
            >
          </div>
          <el-tag :type="currentBatch.complete ? 'success' : 'warning'"
            >已上传 {{ currentBatch.uploadedCount }}/{{
              currentBatch.expectedCount
            }}</el-tag
          >
        </div>
        <p v-if="currentBatch.missingSequences?.length" class="batch-pending">
          待上传序号：{{
            currentBatch.missingSequences.join("、")
          }}，后续上传后会归入这一批次。<el-button
            link
            type="primary"
            :loading="detailLoading"
            @click="refreshCurrentBatch"
            >刷新批次</el-button
          >
        </p>
        <p v-if="currentBatch.duplicateSequences?.length" class="batch-pending">
          序号
          {{ currentBatch.duplicateSequences.join("、") }}
          有重复上传，全部记录均保留，可逐张查看。
        </p>
        <div class="batch-switcher">
          <el-button :disabled="activeImageIndex === 0" @click="switchImage(-1)"
            >上一张</el-button
          >
          <span
            >第 {{ activeImageIndex + 1 }} /
            {{ currentBatch.images?.length || 0 }} 张<span
              v-if="currentResult?.sequence"
            >
              · 序号 {{ currentResult.sequence }}</span
            ></span
          >
          <el-button
            :disabled="
              activeImageIndex >= (currentBatch.images?.length || 0) - 1
            "
            @click="switchImage(1)"
            >下一张</el-button
          >
        </div>
        <div class="batch-thumbnails">
          <button
            v-for="(image, index) in currentBatch.images"
            :key="image.id"
            type="button"
            :class="{ active: activeImageIndex === index }"
            :aria-label="`查看第${index + 1}张图片`"
            :aria-pressed="activeImageIndex === index"
            @click="activeImageIndex = index"
          >
            <DataImage
              v-if="image.originalImage"
              :image-id="image.id"
              :version="image.originalImage"
              :preview="false"
              thumbnail
              fit="cover"
            />
            <span>{{
              image.sequence ? "序号 " + image.sequence : "图片 " + (index + 1)
            }}</span>
          </button>
        </div>
      </div>
      <div
        v-if="currentResult"
        :key="currentResult.id"
        v-loading="detailLoading"
        class="detail-page"
      >
        <section class="detail-section basic-section">
          <div>
            <span class="meta-label">机器名</span>
            <strong>{{ recordDeviceName(currentResult) }}</strong>
          </div>
          <div>
            <span class="meta-label">检测时间</span>
            <strong>{{ formatDate(currentResult.createdTime) }}</strong>
          </div>
          <div>
            <span class="meta-label">识别数量</span>
            <strong>{{ getShrimpCount(currentResult) ?? "-" }} 只</strong>
          </div>
          <div>
            <span class="meta-label">当前文件</span
            ><strong class="batch-filename">{{
              currentResult.originalFilename || "-"
            }}</strong>
          </div>
        </section>

        <section class="detail-section">
          <h3>识别对比</h3>
          <div class="image-grid">
            <div class="image-panel">
              <span>原始图片</span>
              <DataImage
                v-if="currentResult.originalImage"
                :image-id="currentResult.id"
                :version="currentResult.originalImage"
                fit="contain"
              />
              <div v-else class="empty-image">暂无图片</div>
            </div>
            <div class="image-panel">
              <span>识别结果</span>
              <DataImage
                v-if="currentResult.annotatedImage"
                :image-id="currentResult.id"
                :version="currentResult.annotatedImage"
                variant="annotated"
                fit="contain"
              />
              <div v-else class="empty-image">暂无图片</div>
            </div>
          </div>
        </section>

        <section
          v-if="getShrimpCount(currentResult) === 0"
          class="detail-section empty-result"
        >
          <h3>未检测到虾</h3>
          <p>
            本次图片未识别到虾体，因此没有单虾评分和综合判断。请上传包含清晰虾体的图片重新检测。
          </p>
        </section>

        <template v-else-if="hasStructuredResult(currentResult)">
          <section class="detail-section">
            <div class="section-heading">
              <h3>本次判断概览</h3>
              <span>判断结果由模型服务提供</span>
            </div>
            <div class="overview-grid">
              <div
                v-for="item in getAssessmentSummary(currentResult)"
                :key="item.code"
                class="overview-item"
                :class="`overview-${item.code}`"
              >
                <strong>{{ item.count }}</strong>
                <span>{{ item.label }}</span>
              </div>
            </div>
          </section>

          <section class="detail-section">
            <h3>单虾检测结果</h3>
            <div class="shrimp-grid">
              <article
                v-for="shrimp in getShrimps(currentResult)"
                :key="shrimp.shrimp_id"
                class="shrimp-card"
              >
                <header>
                  <div>
                    <span class="shrimp-number">虾 {{ shrimp.shrimp_id }}</span>
                    <small>长度 {{ formatLength(shrimp.actual_length) }}</small>
                  </div>
                  <el-tag
                    :type="getOverallTagType(shrimp.overall?.code)"
                    effect="dark"
                  >
                    {{ shrimp.overall?.label || "未返回综合判断" }}
                  </el-tag>
                </header>

                <div class="confidence-line">
                  <span>目标检测置信度</span>
                  <strong>{{ formatPercent(shrimp.confidence) }}</strong>
                </div>

                <div class="score-list">
                  <div
                    v-for="scoreKey in scoreOrder"
                    :key="scoreKey"
                    class="score-row"
                  >
                    <div class="score-title">
                      <span>{{ scoreDefinitions[scoreKey].label }}</span>
                      <el-tag
                        :type="
                          getScoreTagType(scoreKey, shrimp.scores?.[scoreKey])
                        "
                        size="small"
                      >
                        {{ formatScore(shrimp.scores?.[scoreKey]) }}
                      </el-tag>
                    </div>
                    <p>
                      {{
                        getScoreDescription(scoreKey, shrimp.scores?.[scoreKey])
                      }}
                    </p>
                    <small
                      v-if="
                        shrimp.score_details?.[scoreKey]?.confidence !==
                        undefined
                      "
                    >
                      评分置信度
                      {{
                        formatPercent(shrimp.score_details[scoreKey].confidence)
                      }}
                    </small>
                  </div>
                </div>
              </article>
            </div>
          </section>
        </template>

        <section v-else class="detail-section legacy-section">
          <h3>检测结果</h3>
          <p>该记录未提供单虾评分，以下为当时保存的检测信息。</p>
          <pre>{{ currentResult.result || "暂无检测结果" }}</pre>
        </section>
      </div>

      <template #footer>
        <el-button
          v-if="currentResult"
          type="danger"
          plain
          :icon="Delete"
          @click="handleDelete(currentResult, true)"
          >删除当前图片</el-button
        >
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { computed, reactive, ref, watch } from "vue";
import { useStore } from "vuex";
import { ElMessage, ElMessageBox } from "element-plus";
import zhCn from "element-plus/es/locale/lang/zh-cn";
import DataImage from "@/components/DataImage.vue";
import { recordDeviceName } from "@/utils/deviceNames";
import {
  DataAnalysis,
  Delete,
  Refresh,
  Search,
  View,
} from "@element-plus/icons-vue";
import {
  getDeviceResultBatch,
  countDeviceResultBatches,
  getDeviceResultAvailableDates,
  pageDeviceResultBatches,
  removeDeviceResultBatchImage,
} from "@/api/deviceResult";
import {
  buildDetectionDateRange,
  formatLocalDate,
} from "@/utils/detectionDates";

const scoreDefinitions = {
  H: {
    label: "肝胰腺",
    descriptions: { 0: "透明、轮廓清楚，呈棕褐色", 1: "模糊不清或出现异色" },
  },
  M: {
    label: "腹部",
    descriptions: {
      0: "清晰透明",
      1: "轻微棕黑色",
      2: "有部分异色",
      3: "严重异色",
    },
  },
  C: {
    label: "全身颜色",
    descriptions: { 0: "总体颜色正常", 1: "颜色轻微异常", 2: "总体颜色异常" },
  },
};

const scoreOrder = ["H", "M", "C"];

export default {
  name: "DeviceResultList",
  components: { DataImage },
  setup() {
    const store = useStore();
    const loading = ref(false);
    const currentPage = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const counting = ref(false);
    const resultList = ref([]);
    const detailDialogVisible = ref(false);
    const currentBatch = ref(null);
    const activeImageIndex = ref(0);
    const detailLoading = ref(false);
    const currentResult = computed(
      () => currentBatch.value?.images?.[activeImageIndex.value] || null
    );
    const searchForm = reactive({ deviceId: "" });
    const dateMode = ref("day");
    const selectedDate = ref(null);
    const availableDates = ref([]);
    const calendarLoading = ref(false);
    const availableDateSet = computed(() => new Set(availableDates.value));
    const isDateDisabled = (date) =>
      calendarLoading.value ||
      !availableDateSet.value.has(formatLocalDate(date));
    const datePickerType = computed(
      () =>
        ({ day: "date", month: "month", year: "year", range: "daterange" }[
          dateMode.value
        ])
    );
    const dateDisplayFormat = computed(
      () => ({ month: "YYYY-MM", year: "YYYY" }[dateMode.value] || "YYYY-MM-DD")
    );
    const datePlaceholder = computed(
      () =>
        ({ day: "选择检测日期", month: "选择检测月份", year: "选择检测年份" }[
          dateMode.value
        ])
    );
    const calendarDefaultDate = computed(() => {
      const latest = availableDates.value[availableDates.value.length - 1];
      if (!latest) return new Date();
      const [year, month, day] = latest.split("-").map(Number);
      return new Date(year, month - 1, day);
    });
    let listRequestId = 0;
    let calendarRequestId = 0;
    let detailRequestId = 0;
    let countRequestId = 0;
    let countKey = "";
    let exactCount = null;
    let countPromise = null;
    const invalidateExactCount = () => {
      countKey = "";
      exactCount = null;
      countPromise = null;
      countRequestId++;
    };
    const cancelDetailLoad = () => {
      detailRequestId++;
      detailLoading.value = false;
    };

    const updateExactCount = (params, requestId) => {
      const filters = {
        deviceId: params.deviceId,
        startDate: params.startDate,
        endDate: params.endDate,
      };
      const key = JSON.stringify(filters);
      if (key !== countKey) {
        countKey = key;
        exactCount = null;
        countPromise = null;
        countRequestId++;
      }
      if (exactCount !== null) {
        total.value = exactCount;
        counting.value = false;
        return;
      }
      counting.value = true;
      const generation = countRequestId;
      if (!countPromise)
        countPromise = countDeviceResultBatches(filters)
          .then((response) => {
            if (response.code !== 0)
              throw new Error(response.message || "总数统计失败");
            if (generation === countRequestId)
              exactCount = Number(response.data) || 0;
            return Number(response.data) || 0;
          })
          .finally(() => {
            if (generation === countRequestId) countPromise = null;
          });
      countPromise
        .then((value) => {
          if (generation === countRequestId && requestId === listRequestId) {
            total.value = value;
            counting.value = false;
          }
        })
        .catch(() => {
          if (generation === countRequestId) counting.value = false;
        });
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return "-";
      const normalized =
        typeof dateStr === "string" ? dateStr.replace(" ", "T") : dateStr;
      const date = new Date(normalized);
      if (Number.isNaN(date.getTime())) {
        return typeof dateStr === "string" ? dateStr.replace("T", " ") : "-";
      }
      return date.toLocaleString("zh-CN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    };

    const parseStructuredStatistics = (statistics) => {
      if (!statistics) return null;

      const countMatch = statistics.match(/检测到虾的数量[：:]\s*(\d+)/);
      if (countMatch && Number(countMatch[1]) === 0)
        return { total_shrimp: 0, shrimps: [] };

      const shrimps = [];
      const pattern =
        /虾\s*(\d+)[：:]\s*M=(X|\d+).*?H=(X|\d+).*?C=(X|\d+).*?外观总评[：:]([^\r\n]+)[\r\n]+\s*实际长度[：:]\s*([\d.]+)/gs;
      let match;
      while ((match = pattern.exec(statistics)) !== null) {
        const label = match[5].trim();
        let code = "insufficient";
        if (label.includes("基本正常")) code = "normal";
        if (label.includes("明显异常")) code = "abnormal";
        else if (label.includes("可疑")) code = "suspect";
        else if (label.includes("不足")) code = "insufficient";

        const parseScore = (value) => (value === "X" ? null : Number(value));
        shrimps.push({
          shrimp_id: Number(match[1]),
          actual_length: Number(match[6]),
          confidence: null,
          scores: {
            M: parseScore(match[2]),
            H: parseScore(match[3]),
            C: parseScore(match[4]),
          },
          score_details: {},
          overall: { code, label },
        });
      }

      // 旧记录仍可展示每只虾的长度；未返回的评分保持空值。
      if (!shrimps.length) {
        const legacyPattern = /虾\s*(\d+)[：:]\s*实际长度[：:]\s*([\d.]+)/g;
        while ((match = legacyPattern.exec(statistics)) !== null) {
          shrimps.push({
            shrimp_id: Number(match[1]),
            actual_length: Number(match[2]),
            confidence: null,
            scores: {},
            overall: { code: "insufficient", label: "未返回评分" },
          });
        }
      }

      return shrimps.length
        ? { total_shrimp: shrimps.length, total_vein: 0, shrimps }
        : null;
    };

    const getResultData = (row) =>
      row?.resultData || parseStructuredStatistics(row?.result);
    const getShrimps = (row) => {
      const shrimps = getResultData(row)?.shrimps;
      return Array.isArray(shrimps) ? shrimps : [];
    };
    const hasStructuredResult = (row) => !!getResultData(row);

    const getShrimpCount = (row) => {
      const resultData = getResultData(row);
      if (resultData && resultData.total_shrimp !== undefined)
        return resultData.total_shrimp;
      const match = row?.result?.match(/检测到虾的数量[：:]\s*(\d+)/);
      return match ? Number(match[1]) : null;
    };

    const getAssessmentSummary = (row) => {
      const counts = new Map();
      getShrimps(row).forEach((shrimp) => {
        const code = shrimp.overall?.code || "insufficient";
        const label = shrimp.overall?.label || "未返回综合判断";
        const current = counts.get(code) || { code, label, count: 0 };
        current.count += 1;
        counts.set(code, current);
      });
      return Array.from(counts.values());
    };

    const getOverallTagType = (code) => {
      const types = {
        normal: "success",
        suspect: "warning",
        abnormal: "danger",
        insufficient: "info",
      };
      return types[code] || "info";
    };

    const getScoreTagType = (key, score) => {
      if (score === null || score === undefined) return "info";
      if (Number(score) === 0) return "success";
      if (
        (key === "M" && Number(score) >= 2) ||
        (key === "C" && Number(score) >= 2)
      )
        return "danger";
      return "warning";
    };

    const formatScore = (score) =>
      score === null || score === undefined ? "无法判断" : `${score} 分`;

    const getScoreDescription = (key, score) => {
      if (score === null || score === undefined)
        return "该项未得到有效识别结果";
      return scoreDefinitions[key]?.descriptions?.[score] || "暂无说明";
    };

    const formatPercent = (value) => {
      if (value === null || value === undefined || value === "") return "-";
      const number = Number(value);
      return Number.isFinite(number) ? `${(number * 100).toFixed(1)}%` : "-";
    };

    const formatLength = (value) => {
      if (value === null || value === undefined || value === "") return "-";
      const number = Number(value);
      return Number.isFinite(number) ? `${number.toFixed(2)} mm` : "-";
    };

    const fetchResultList = async () => {
      const requestId = ++listRequestId;
      loading.value = true;
      try {
        const params = {
          pageNum: currentPage.value,
          pageSize: pageSize.value,
          deviceId: searchForm.deviceId.trim() || undefined,
          ...buildDetectionDateRange(dateMode.value, selectedDate.value),
        };
        const res = await pageDeviceResultBatches(params);
        if (requestId !== listRequestId) return;
        if (res.code === 0 && res.data) {
          resultList.value = res.data.records || [];
          total.value = Number.parseInt(res.data.totalRow, 10) || 0;
          updateExactCount(params, requestId);
        } else {
          resultList.value = [];
          total.value = 0;
          ElMessage.error(res.message || "获取检测结果列表失败");
        }
      } catch (error) {
        if (requestId !== listRequestId) return;
        resultList.value = [];
        total.value = 0;
        console.error("获取检测结果列表异常:", error);
        ElMessage.error("获取检测结果列表失败");
      } finally {
        if (requestId === listRequestId) loading.value = false;
      }
    };

    const fetchAvailableDates = async () => {
      const requestId = ++calendarRequestId;
      calendarLoading.value = true;
      try {
        const res = await getDeviceResultAvailableDates({
          deviceId: searchForm.deviceId.trim() || undefined,
        });
        if (requestId !== calendarRequestId) return;
        if (res.code !== 0) throw new Error(res.message || "获取检测日期失败");
        availableDates.value = Array.isArray(res.data)
          ? [...res.data].sort()
          : [];
      } catch (error) {
        if (requestId !== calendarRequestId) return;
        availableDates.value = [];
        ElMessage.error(error.message || "获取检测日期失败");
      } finally {
        if (requestId === calendarRequestId) calendarLoading.value = false;
      }
    };

    const handleSearch = () => {
      invalidateExactCount();
      currentPage.value = 1;
      fetchResultList();
      fetchAvailableDates();
    };
    const handleDateModeChange = () => {
      selectedDate.value = null;
      handleSearch();
    };
    const handleRefresh = () => {
      searchForm.deviceId = "";
      selectedDate.value = null;
      handleSearch();
    };

    const handleViewDetail = async (row) => {
      const requestId = ++detailRequestId;
      detailLoading.value = true;
      currentBatch.value = null;
      detailDialogVisible.value = true;
      try {
        const res = await getDeviceResultBatch(row.batchAnchorId || row.id);
        if (requestId !== detailRequestId) return;
        if (res.code === 0) {
          currentBatch.value = res.data;
          activeImageIndex.value = 0;
          detailDialogVisible.value = true;
        } else {
          ElMessage.error(res.message || "获取详情失败");
        }
      } catch (error) {
        if (requestId !== detailRequestId) return;
        console.error("获取详情异常:", error);
        ElMessage.error("获取详情失败");
      } finally {
        if (requestId === detailRequestId) detailLoading.value = false;
      }
    };

    const switchImage = (step) => {
      activeImageIndex.value = Math.max(
        0,
        Math.min(
          (currentBatch.value?.images?.length || 1) - 1,
          activeImageIndex.value + step
        )
      );
    };

    const refreshCurrentBatch = async () => {
      if (!currentResult.value) return;
      const id = currentResult.value.id;
      detailLoading.value = true;
      try {
        const res = await getDeviceResultBatch(id);
        if (res.code !== 0) throw new Error(res.message || "刷新批次失败");
        currentBatch.value = res.data;
        const index = res.data.images.findIndex(
          (image) => String(image.id) === String(id)
        );
        activeImageIndex.value = Math.max(0, index);
        invalidateExactCount();
        fetchResultList();
      } catch (error) {
        ElMessage.error(error.message || "刷新批次失败");
      } finally {
        detailLoading.value = false;
      }
    };

    const handleDelete = (row, fromDetail = false) => {
      return ElMessageBox.confirm(
        `确定删除设备 ${recordDeviceName(
          row
        )} 的当前图片及其检测结果吗？同批次其他图片会保留。`,
        "提示",
        { confirmButtonText: "确定", cancelButtonText: "取消", type: "warning" }
      )
        .then(async () => {
          const res = await removeDeviceResultBatchImage(row.id);
          if (res.code === 0) {
            ElMessage.success("删除成功");
            invalidateExactCount();
            fetchResultList();
            fetchAvailableDates();
            if (fromDetail) {
              const remaining = currentBatch.value.images.filter(
                (image) => String(image.id) !== String(row.id)
              );
              if (!remaining.length) {
                detailDialogVisible.value = false;
                currentBatch.value = null;
              } else {
                const oldIndex = activeImageIndex.value;
                const next = await getDeviceResultBatch(remaining[0].id);
                if (next.code === 0) {
                  currentBatch.value = next.data;
                  activeImageIndex.value = Math.min(
                    oldIndex,
                    next.data.images.length - 1
                  );
                } else {
                  detailDialogVisible.value = false;
                  ElMessage.error(next.message || "请重新打开批次");
                }
              }
            }
          } else {
            ElMessage.error(res.message || "删除失败");
          }
        })
        .catch((error) => {
          if (error !== "cancel" && error !== "close")
            ElMessage.error("操作失败，请刷新后重试");
        });
    };

    const handleSizeChange = (value) => {
      pageSize.value = value;
      currentPage.value = 1;
      fetchResultList();
    };
    const handleCurrentChange = (value) => {
      currentPage.value = value;
      fetchResultList();
    };

    watch(
      () => [store.state.user?.id, store.getters.userRole],
      ([userId, role]) => {
        if (userId && role) {
          handleRefresh();
        }
      },
      { immediate: true }
    );

    return {
      recordDeviceName,
      zhCn,
      DataAnalysis,
      Delete,
      Refresh,
      Search,
      View,
      currentPage,
      dateMode,
      selectedDate,
      datePickerType,
      dateDisplayFormat,
      datePlaceholder,
      calendarDefaultDate,
      calendarLoading,
      isDateDisabled,
      handleDateModeChange,
      currentResult,
      currentBatch,
      activeImageIndex,
      detailLoading,
      switchImage,
      refreshCurrentBatch,
      detailDialogVisible,
      formatDate,
      formatLength,
      formatPercent,
      formatScore,
      getAssessmentSummary,
      getOverallTagType,
      getScoreDescription,
      getScoreTagType,
      getShrimpCount,
      getShrimps,
      handleCurrentChange,
      handleDelete,
      handleRefresh,
      handleSearch,
      handleSizeChange,
      handleViewDetail,
      hasStructuredResult,
      loading,
      pageSize,
      resultList,
      scoreDefinitions,
      scoreOrder,
      searchForm,
      total,
      counting,
      cancelDetailLoad,
    };
  },
};
</script>

<style scoped>
.batch-list-hint {
  margin: 0 0 14px;
  color: #87928f;
  font-size: 12px;
}
.batch-status {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}
.batch-status small {
  color: #9a7a3a;
  font-size: 11px;
}
.batch-navigation {
  margin-bottom: 16px;
  padding: 16px;
  border-radius: 12px;
  background: #f0f9f5;
}
.batch-navigation-heading,
.batch-switcher {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}
.batch-navigation-heading > div {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.batch-navigation-heading span {
  font-size: 12px;
  color: #73817d;
}
.batch-switcher {
  justify-content: center;
  margin: 15px 0 12px;
}
.batch-switcher > span {
  min-width: 155px;
  text-align: center;
  color: #267b65;
  font-weight: 600;
}
.batch-pending {
  margin: 10px 0 0;
  color: #a37328;
  font-size: 12px;
}
.batch-thumbnails {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 4px;
}
.batch-thumbnails button {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 92px;
  padding: 5px;
  border: 2px solid transparent;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  color: #73817d;
  font-size: 12px;
}
.batch-thumbnails button.active {
  border-color: #1b9a7c;
  color: #16866e;
}
.batch-thumbnails .data-image {
  width: 76px;
  height: 52px;
  border-radius: 4px;
  object-fit: cover;
}
.batch-filename {
  overflow-wrap: anywhere;
}
.page-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e0e0e0;
  color: #16866e;
  font-size: 20px;
}
.page-card,
.detail-section {
  background: #fff;
  border: 1px solid #e8efec;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(20, 99, 81, 0.06);
}
.page-card {
  padding: 20px;
}
.table-operations,
.row-actions,
.summary-tags,
.section-heading,
.confidence-line,
.score-title {
  display: flex;
  align-items: center;
}
.table-operations {
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}
.query-controls,
.date-controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}
.query-controls {
  flex: 1;
  min-width: 0;
}
.date-mode {
  width: 110px;
}
.date-controls :deep(.result-date-picker) {
  width: 180px;
  flex-grow: 0;
}
.date-controls :deep(.result-date-picker.is-range) {
  width: 280px;
}
.calendar-hint {
  color: #87928f;
  font-size: 12px;
}
.summary-tags {
  gap: 8px;
  flex-wrap: wrap;
}
.row-actions {
  justify-content: center;
  flex-wrap: nowrap;
  gap: 8px;
  white-space: nowrap;
}
.row-actions :deep(.el-button) {
  margin-left: 0;
  flex-shrink: 0;
}
.empty-result p {
  color: #66736f;
  line-height: 1.7;
  margin: 0;
}
.table-image {
  width: 56px;
  height: 56px;
  border-radius: 8px;
}
.count-value {
  color: #16866e;
  font-size: 17px;
  font-weight: 700;
}
.legacy-label,
.section-heading span,
.meta-label,
.confidence-line span,
.score-row small {
  color: #87928f;
}
.pagination {
  margin-top: 20px;
  text-align: right;
}
.detail-page {
  display: grid;
  gap: 16px;
  max-height: 55vh;
  padding: 2px;
  overflow-y: auto;
}
.detail-section {
  padding: 20px;
}
.detail-section h3 {
  margin: 0 0 16px;
  color: #263b36;
  font-size: 17px;
}
.basic-section {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  background: linear-gradient(135deg, #f1fbf7, #f8fcfb);
}
.basic-section div {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.image-grid,
.shrimp-grid,
.overview-grid {
  display: grid;
  gap: 14px;
}
.image-grid {
  grid-template-columns: repeat(2, 1fr);
}
.image-panel {
  padding: 12px;
  border-radius: 10px;
  background: #f6f8f7;
}
.image-panel > span {
  display: block;
  margin-bottom: 10px;
  color: #66736f;
  font-size: 13px;
}
.image-panel .data-image,
.empty-image {
  width: 100%;
  height: 260px;
}
.empty-image {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a1aaa7;
}
.section-heading {
  justify-content: space-between;
}
.overview-grid {
  grid-template-columns: repeat(4, minmax(120px, 1fr));
}
.overview-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px;
  border-radius: 10px;
  background: #f5f7f6;
}
.overview-item strong {
  font-size: 25px;
}
.overview-normal {
  background: #eaf8f0;
  color: #258654;
}
.overview-suspect {
  background: #fff6e6;
  color: #b46b0b;
}
.overview-abnormal {
  background: #feeeee;
  color: #c83d3d;
}
.overview-insufficient {
  background: #f0f2f4;
  color: #6e777d;
}
.shrimp-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.shrimp-card {
  overflow: hidden;
  border: 1px solid #e1e9e6;
  border-radius: 12px;
  background: #fff;
}
.shrimp-card header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 16px;
  background: #f4faf7;
}
.shrimp-card header > div {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.shrimp-number {
  color: #173f35;
  font-size: 17px;
  font-weight: 700;
}
.shrimp-card header small {
  color: #73817d;
}
.confidence-line {
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #eef2f0;
}
.score-list {
  padding: 5px 16px 14px;
}
.score-row {
  padding: 12px 0;
  border-bottom: 1px dashed #e5ebe8;
}
.score-row:last-child {
  border-bottom: 0;
}
.score-title {
  justify-content: space-between;
  font-weight: 600;
}
.score-row p {
  margin: 6px 0 4px;
  color: #53615d;
  font-size: 13px;
}
.legacy-section pre {
  margin: 0;
  padding: 16px;
  overflow: auto;
  border-radius: 8px;
  background: #f7f8f8;
  color: #4c5955;
  font: inherit;
  line-height: 1.7;
  white-space: pre-wrap;
}
@media (max-width: 900px) {
  .shrimp-grid,
  .image-grid {
    grid-template-columns: 1fr;
  }
  .overview-grid,
  .basic-section {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>

<style>
.el-image-viewer__wrapper {
  z-index: 9999 !important;
}
</style>
